import { Injectable } from '@angular/core';
import { TenantContextService } from './tenant-context.service';

export type LocalStorageScope = 'account' | 'browser';

/** Persistent preferences use tenant/user identity, never the login generation. */
@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  readonly sessionChanged$;

  constructor(private readonly tenant: TenantContextService) {
    this.sessionChanged$ = tenant.sessionChanged$;
  }

  get<T>(key: string, scope: LocalStorageScope = 'account'): T | null {
    const raw = this.getItem(key, scope);
    if (raw === null) return null;
    try { return JSON.parse(raw) as T; } catch { return null; }
  }

  set<T>(key: string, value: T, scope: LocalStorageScope = 'account'): boolean {
    try {
      const raw = JSON.stringify(value);
      return raw !== undefined && this.setItem(key, raw, scope);
    } catch { return false; }
  }

  // Raw string access also preserves the existing browser-wide auth token format.
  getItem(key: string, scope: LocalStorageScope = 'account'): string | null {
    const resolved = this.key(key, scope);
    if (resolved === null) return null;
    try { return localStorage.getItem(resolved); } catch { return null; }
  }

  setItem(key: string, value: string, scope: LocalStorageScope = 'account'): boolean {
    const resolved = this.key(key, scope);
    if (resolved === null) return false;
    try { localStorage.setItem(resolved, value); return true; } catch { return false; }
  }

  removeItem(key: string, scope: LocalStorageScope = 'account'): boolean {
    const resolved = this.key(key, scope);
    if (resolved === null) return false;
    try { localStorage.removeItem(resolved); return true; } catch { return false; }
  }

  private key(key: string, scope: LocalStorageScope): string | null {
    if (scope === 'browser') return key;
    const identity = this.tenant.current;
    if (!identity) return null;
    return `br:account-storage:v1:${JSON.stringify([identity.companyId, identity.userId, key])}`;
  }
}
